import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { X, Zap, ArrowUpRight, Loader2, Check } from 'lucide-react';
import type { PlanLimitResult } from '../../hooks/usePlanLimits';

const RECAPTCHA_SITE_KEY = '6LcVLIYsAAAAADaw3VMLLs4jXulXVwzwsp3qdex6';

async function getRecaptchaToken(action: string): Promise<string> {
  return new Promise((resolve, reject) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action });
        resolve(token);
      } catch (e) {
        reject(e);
      }
    });
  });
}

const UPGRADE_PLANS = [
  {
    tier: 'BASIC',
    name: 'Basic',
    price: '$4.99/mo',
    priceId: (import.meta as any).env?.VITE_STRIPE_BASIC_PRICE_ID ?? '',
    features: ['500 active tasks', 'Up to 6 members', 'All core modules'],
    highlighted: false,
  },
  {
    tier: 'PREMIUM',
    name: 'Premium',
    price: '$9.99/mo',
    priceId: (import.meta as any).env?.VITE_STRIPE_PREMIUM_PRICE_ID ?? '',
    features: ['5,000 active tasks', 'Up to 12 members', 'AI Assistant', 'All modules'],
    highlighted: true,
  },
] as const;

const RESOURCE_LABELS: Record<string, string> = {
  tasks: 'active tasks',
  members: 'household members',
  households: 'households',
};

const TIER_LABELS: Record<string, string> = {
  FREE: 'Free',
  BASIC: 'Basic',
  PREMIUM: 'Premium',
};

export interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  result: (PlanLimitResult & { allowed: false }) | null;
  /** Household to subscribe. Required to launch Stripe Checkout inline. */
  householdId?: string | null;
}

/**
 * Inline upgrade prompt shown when a user hits a plan limit.
 * Launches Stripe Checkout directly rather than navigating away.
 */
export function UpgradeModal({ open, onClose, result, householdId }: UpgradeModalProps): React.ReactElement | null {
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  if (!open || !result) return null;

  const tierLabel = TIER_LABELS[result.tier] ?? result.tier;
  const resourceKey = Object.keys(RESOURCE_LABELS).find((k) =>
    result.upgradeUrl?.includes(k)
  ) ?? 'tasks';
  const resourceLabel = RESOURCE_LABELS[resourceKey];

  const handleUpgrade = async (priceId: string) => {
    if (!priceId || !householdId) {
      // Fallback: navigate to upgrade page
      window.location.href = result.upgradeUrl ?? '/upgrade';
      return;
    }
    setIsUpgrading(true);
    setCheckoutError(null);
    try {
      const recaptchaToken = await getRecaptchaToken('checkout');
      const fns = getFunctions(getApp());
      const createSession = httpsCallable(fns, 'createCheckoutSession');
      const res = await createSession({ priceId, householdId, recaptchaToken });
      const { url } = res.data as { url: string };
      window.location.href = url;
    } catch {
      setCheckoutError('Failed to start checkout. Please try again.');
      setIsUpgrading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
      onClick={onClose}
    >
      <div
        className="relative mx-4 w-full max-w-md rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-2xl border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2.5 bg-amber-100 dark:bg-amber-900/30 rounded-2xl">
            <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h2
              id="upgrade-modal-title"
              className="font-black text-slate-900 dark:text-white text-lg leading-tight"
            >
              Plan Limit Reached
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Your <span className="font-bold">{tierLabel}</span> plan allows up to{' '}
              <span className="font-bold">{result.limit} {resourceLabel}</span>.
              You currently have <span className="font-bold">{result.current}</span>.
            </p>
          </div>
        </div>

        {checkoutError && (
          <p className="text-sm text-red-500 mb-4 bg-red-50 dark:bg-red-900/20 rounded-xl py-2 px-3 text-center">
            {checkoutError}
          </p>
        )}

        {/* Plan cards */}
        <div className="space-y-3">
          {UPGRADE_PLANS.map((plan) => (
            <div
              key={plan.tier}
              className={`rounded-2xl p-4 border ${
                plan.highlighted
                  ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20'
                  : 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span
                    className={`font-black text-sm ${
                      plan.highlighted
                        ? 'text-amber-700 dark:text-amber-400'
                        : 'text-blue-700 dark:text-blue-400'
                    }`}
                  >
                    {plan.name}
                  </span>
                  {plan.highlighted && (
                    <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-amber-200 dark:bg-amber-800 text-amber-700 dark:text-amber-300 rounded">
                      Popular
                    </span>
                  )}
                </div>
                <span
                  className={`font-black text-sm ${
                    plan.highlighted
                      ? 'text-amber-700 dark:text-amber-400'
                      : 'text-blue-700 dark:text-blue-400'
                  }`}
                >
                  {plan.price}
                </span>
              </div>

              <ul className="space-y-1 mb-3">
                {plan.features.map((f) => (
                  <li key={f} className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                    <Check className="w-3 h-3 text-emerald-500 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleUpgrade(plan.priceId)}
                disabled={isUpgrading}
                className={`w-full h-10 rounded-xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 transition-all disabled:opacity-50 ${
                  plan.highlighted
                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {isUpgrading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>
                    Upgrade to {plan.name}
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          className="w-full mt-4 text-xs font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}
